/**
 * AddonGate
 *
 * Renders `children` when the named add-on is active.
 * While the subscription is still loading it renders children optimistically
 * (avoids a jarring flash for stores that DO have the add-on).
 * Once loaded, if the add-on is not active it replaces the content with a
 * rich upsell landing page.
 *
 * Usage:
 *   <AddonGate addonName="pallet workforce">
 *     <StaffManagementScreen />
 *   </AddonGate>
 */

import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../../context/SubscriptionContext";
import { UserContext } from "../../App";
import "./index.css";

/* ── Upsell feature cards ─────────────────────────────── */
const FEATURES = [
  {
    label: "Shift scheduling",
    desc: "Plan and publish shifts in minutes. Let staff swap or pick up open slots.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
        <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
      </svg>
    ),
  },
  {
    label: "Attendance tracking",
    desc: "Clock-in / clock-out with geo-fencing and automatic overtime alerts.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  {
    label: "Role management",
    desc: "Assign roles, permissions and pay grades — synced with your POS access controls.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Performance insights",
    desc: "Sales-per-hour, upsell rate, and punctuality scores for every team member.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
];

/* ── Stats row data ───────────────────────────────────── */
const STATS = [
  { num: "40%", label: "Less scheduling time" },
  { num: "2×", label: "Faster onboarding" },
  { num: "99%", label: "Attendance accuracy" },
];

/* ── People / staff SVG icon ──────────────────────────── */
function PeopleIcon({ size = 36 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/* ── Upsell page ──────────────────────────────────────── */
function UpsellPage({ addonName }) {
  const { isDarkMode } = useContext(UserContext) || {};
  const navigate = useNavigate();

  const handleTalkToSales = () => {
    window.dispatchEvent(new CustomEvent("pos:open-help-and-support"));
  };

  return (
    <div className={`addon-gate-upsell${isDarkMode ? " dark" : ""}`}>
      {/* Hero */}
      <div className="addon-gate-hero">
        <button
          className="addon-gate-back-btn"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/products/operations-menu");
            }
          }}
          type="button"
          aria-label="Go back"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-arrow-left w-5 h-5"
            aria-hidden="true"
          >
            <path d="m12 19-7-7 7-7"></path>
            <path d="M19 12H5"></path>
          </svg>
        </button>
        <div className="addon-gate-icon-wrap">
          <PeopleIcon size={36} />
        </div>
        <span className="addon-gate-badge">Paid Add-On</span>
        <h1 className="addon-gate-title">Pallet Workforce</h1>
        <p className="addon-gate-subtitle">
          Everything you need to schedule, track and grow your retail team — built right into your
          POS.
        </p>

        {/* Stats row */}
        <div className="addon-gate-stats">
          {STATS?.map((s) => (
            <div key={s?.label} className="addon-gate-stat">
              <span className="addon-gate-stat-num">{s?.num}</span>
              <span className="addon-gate-stat-label">{s?.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Feature cards + CTA */}
      <div className="addon-gate-body">
        <p className="addon-gate-section-title">What you get</p>
        <div className="addon-gate-cards">
          {FEATURES?.map((f) => (
            <div key={f?.label} className="addon-gate-card">
              <div className="addon-gate-card-icon">{f?.icon}</div>
              <span className="addon-gate-card-label">{f?.label}</span>
              <span className="addon-gate-card-desc">{f?.desc}</span>
            </div>
          ))}
        </div>

        <div className="addon-gate-cta">
          <button className="addon-gate-btn-primary" onClick={handleTalkToSales}>
            Talk to Sales
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── AddonGate (default export) ───────────────────────── */
export default function AddonGate({ addonName, children }) {
  const { bundle, isLoaded } = useSubscription();

  /* Optimistic: render children while we are still loading */
  if (!isLoaded) return <>{children}</>;

  /* Fuzzy check: addonId or packageName contains the keyword AND status is ACTIVE */
  const kw = String(addonName).toLowerCase();
  const isActive =
    Array.isArray(bundle?.addons) &&
    bundle.addons.some(
      (a) =>
        a?.status === "ACTIVE" &&
        ((a?.addonId && a.addonId.toLowerCase().includes(kw)) ||
          (a?.packageName || "").toLowerCase().includes(kw))
    );

  if (isActive) return <>{children}</>;

  return <UpsellPage addonName={addonName} />;
}
